import { useMemo } from 'react';
import { Box, Typography, Grid, Tooltip, Link, Badge, Chip } from '@mui/material';
import TooltipTitle from './Calendar/TooltipTitle';

// Use unified date utils
import { moment } from '../../utils/dateUtils';

const MonthView = ({ date, appointments, technicians, onDateClick }) => {
  // moment.tz.setDefault is now configured in dateUtils.js

  // Calculate month calendar
  const calendarDays = useMemo(() => {
    const startOfMonth = moment(date).startOf('month');
    const endOfMonth = moment(date).endOf('month');
    const startDate = moment(startOfMonth).startOf('week');
    const endDate = moment(endOfMonth).endOf('week');
    
    const days = [];
    let current = moment(startDate);
    
    while (current.isSameOrBefore(endDate)) {
      days.push({
        date: current.format('YYYY-MM-DD'),
        dayNum: current.format('D'),
        isCurrentMonth: current.month() === moment(date).month(),
        isToday: current.isSame(moment(), 'day'),
      });
      current.add(1, 'day');
    }
    
    return days;
  }, [date]);

  // Group appointments by day
  const appointmentsByDay = useMemo(() => {
    const grouped = {};
    appointments?.forEach(apt => {
      if (apt.status === 'Cancelled') return;
      if (!apt.property && !apt.propertyId) return; // Skip BLOCK TIME (no property)
      const day = moment(apt.startTime).format('YYYY-MM-DD');
      if (!grouped[day]) grouped[day] = [];
      grouped[day].push(apt);
    });
    return grouped;
  }, [appointments]);

  // Count by type
  const countByType = (apts) => {
    const counts = { Installation: 0, Estimate: 0, 'Service Call': 0, Other: 0 };
    apts?.forEach(apt => {
      const type = apt.type?.name;
      if (counts.hasOwnProperty(type)) {
        counts[type]++;
      } else {
        counts.Other++;
      }
    });
    return counts;
  };

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Box>
      {/* Week day headers */}
      <Grid container spacing={0}>
        {weekDays.map((day) => (
          <Grid item key={day} xs={12/7}>
            <Box
              sx={{
                py: 1,
                borderBottom: '2px solid #e0e0e0',
                textAlign: 'center',
                bgcolor: '#f5f5f5',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {day}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Calendar days */}
      <Grid container spacing={0}>
        {calendarDays.map((day, idx) => {
          const dayApts = appointmentsByDay[day.date] || [];
          const counts = countByType(dayApts);
          
          return (
            <Grid item key={day.date} xs={12/7}>
              <Box
                onClick={() => onDateClick?.(day.date)}
                sx={{
                  minHeight: 100,
                  border: '1px solid #e0e0e0',
                  borderTop: 'none',
                  borderLeft: idx % 7 === 0 ? '1px solid #e0e0e0' : 'none',
                  p: 0.5,
                  bgcolor: day.isCurrentMonth ? '#fff' : '#fafafa',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: day.isCurrentMonth ? '#f5f9ff' : '#f0f0f0',
                  },
                }}
              >
                {/* Day number */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: day.isToday ? 700 : 400,
                      color: day.isCurrentMonth ? (day.isToday ? '#1976d2' : 'inherit') : '#bbb',
                      bgcolor: day.isToday ? '#e3f2fd' : 'transparent',
                      borderRadius: '50%',
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {day.dayNum}
                  </Typography>
                  {dayApts.length > 0 && (
                    <Badge 
                      badgeContent={dayApts.length} 
                      color="primary" 
                      sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}
                    />
                  )}
                </Box>

                {/* Appointment counts by type */}
                {dayApts.length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    {counts.Installation > 0 && (
                      <Chip
                        label={`${counts.Installation} Install`}
                        size="small"
                        sx={{
                          height: 16,
                          fontSize: 9,
                          bgcolor: 'purple',
                          color: '#fff',
                          '& .MuiChip-label': { px: 0.5 },
                        }}
                      />
                    )}
                    {counts.Estimate > 0 && (
                      <Chip
                        label={`${counts.Estimate} Est`}
                        size="small"
                        sx={{
                          height: 16,
                          fontSize: 9,
                          bgcolor: '#1976d2',
                          color: '#fff',
                          '& .MuiChip-label': { px: 0.5 },
                        }}
                      />
                    )}
                    {counts['Service Call'] > 0 && (
                      <Chip
                        label={`${counts['Service Call']} Svc`}
                        size="small"
                        sx={{
                          height: 16,
                          fontSize: 9,
                          bgcolor: '#f57c00',
                          color: '#fff',
                          '& .MuiChip-label': { px: 0.5 },
                        }}
                      />
                    )}
                    {counts.Other > 0 && (
                      <Chip
                        label={`${counts.Other} Other`}
                        size="small"
                        sx={{
                          height: 16,
                          fontSize: 9,
                          bgcolor: '#666',
                          color: '#fff',
                          '& .MuiChip-label': { px: 0.5 },
                        }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default MonthView;
